/**
 * Cart Drawer — BuysnacksUK V2
 * Vanilla JS IIFE. No jQuery, no build step.
 * Hooks into Empire via the public `cartcount:update` custom event.
 */
(function () {
  'use strict';

  /* ============================================================
     Selectors
     ============================================================ */
  var SEL = {
    overlay:       '[data-cart-drawer-overlay]',
    panel:         '[data-cart-drawer-panel]',
    close:         '[data-cart-drawer-close]',
    trigger:       '[data-cart-drawer-trigger]',
    body:          '[data-cart-drawer-body]',
    loading:       '[data-cart-drawer-loading]',
    error:         '[data-cart-drawer-error]',
    config:        '[data-cart-drawer-config]',
    count:         '[data-cart-drawer-count]',
    confetti:      '[data-cart-drawer-confetti]',
    // Sections (for AJAX swap)
    sectionItems:    '[data-cart-drawer-section="items"]',
    sectionProgress: '[data-cart-drawer-section="progress"]',
    sectionCheckout: '[data-cart-drawer-section="checkout"]',
    sectionLoyalty:  '[data-cart-drawer-section="loyalty"]',
    sectionExtras:   '[data-cart-drawer-section="extras"]',
    sectionFbt:      '[data-cart-drawer-section="fbt"]',
    sectionSignup:   '[data-cart-drawer-section="signup"]',
    // Items
    item:          '[data-cart-drawer-item]',
    itemKey:       'data-cart-drawer-item-key',
    qtyMinus:      '[data-cart-drawer-qty-minus]',
    qtyPlus:       '[data-cart-drawer-qty-plus]',
    qtyInput:      '[data-cart-drawer-qty-input]',
    remove:        '[data-cart-drawer-remove]',
    removeBtn:     '[data-cart-drawer-remove]',
    // Extras
    dismissLogin:  '[data-cart-drawer-dismiss-login]',
    guestLogin:    '[data-cart-drawer-guest-login]',
    signupCard:    '[data-cart-drawer-signup-card]',
    // FBT
    fbtContainer:  '[data-cart-drawer-fbt]',
    fbtScroll:     '[data-cart-drawer-fbt-scroll]',
    // FBT Detail Panel
    detailPanel:   '[data-cart-drawer-detail-panel]',
    detailBack:    '[data-cart-drawer-detail-back]',
    detailImg:     '[data-cart-drawer-detail-img]',
    detailTitle:   '[data-cart-drawer-detail-title]',
    detailPrice:   '[data-cart-drawer-detail-price]',
    detailDesc:    '[data-cart-drawer-detail-desc]',
    detailLink:    '[data-cart-drawer-detail-link]',
    detailAdd:     '[data-cart-drawer-detail-add]',
    // Delivery tracker
    delivery:      '[data-cart-drawer-delivery]',
    deliveryText:  '[data-cart-drawer-delivery-text]',
    // Progress
    progress:      '[data-cart-drawer-progress]'
  };

  /* ============================================================
     State
     ============================================================ */
  var state = {
    isOpen: false,
    isUpdating: false,
    previousTotal: 0,
    qtyDebounceTimer: null,
    focusTrapFirst: null,
    focusTrapLast: null,
    triggerEl: null,
    fbtCartKeys: '',         // tracks which item keys are in cart so FBT only re-fetches when items change
    closedAt: 0,             // timestamp of last close — prevents cartcount:update from reopening
    dispatchingCount: false  // flag to ignore our own cartcount:update dispatch
  };

  /* ============================================================
     Helpers
     ============================================================ */
  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function getConfig() {
    var el = $(SEL.config);
    if (!el) return {};
    try { return JSON.parse(el.textContent); } catch (e) { console.warn('Cart drawer config parse error:', e); return {}; }
  }

  function fetchJSON(url, opts) {
    return fetch(url, opts).then(function (r) {
      if (!r.ok) throw new Error('Network ' + r.status);
      return r.json();
    });
  }

  function fetchHTML(url) {
    return fetch(url).then(function (r) {
      if (!r.ok) throw new Error('Network ' + r.status);
      return r.text();
    });
  }

  function showError(msg) {
    var el = $(SEL.error);
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    setTimeout(function () { el.classList.remove('is-visible'); }, 4000);
  }

  function setLoading(on) {
    var el = $(SEL.loading);
    if (el) el.classList.toggle('is-active', on);
    state.isUpdating = on;
  }

  /* ============================================================
     Open / Close
     ============================================================ */
  function open(triggerEl) {
    var overlay = $(SEL.overlay);
    var panel = $(SEL.panel);
    if (!overlay || !panel || state.isOpen) return;

    state.isOpen = true;
    state.triggerEl = triggerEl || null;

    overlay.classList.add('is-active');
    panel.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');

    // Store previous total for confetti detection
    var config = getConfig();
    state.previousTotal = config.totalPrice || 0;

    // Focus trap
    setupFocusTrap();

    // Delivery tracker
    updateDeliveryTracker();

    // Snapshot current cart keys and fetch FBT on open
    state.fbtCartKeys = $$(SEL.item).map(function (el) {
      return el.getAttribute(SEL.itemKey);
    }).sort().join(',');
    fetchFBT();
  }

  function close() {
    var overlay = $(SEL.overlay);
    var panel = $(SEL.panel);
    if (!overlay || !panel) return;

    state.isOpen = false;
    state.closedAt = Date.now();
    overlay.classList.remove('is-active');
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');

    // Close detail panel if open
    closeDetailPanel();

    // Return focus
    if (state.triggerEl && typeof state.triggerEl.focus === 'function') {
      state.triggerEl.focus();
    }
  }

  function toggle(triggerEl) {
    state.isOpen ? close() : open(triggerEl);
  }

  /* ============================================================
     Focus Trap (Accessibility)
     ============================================================ */
  function setupFocusTrap() {
    var panel = $(SEL.panel);
    if (!panel) return;

    var focusable = panel.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    if (focusable.length === 0) return;

    state.focusTrapFirst = focusable[0];
    state.focusTrapLast = focusable[focusable.length - 1];

    // Focus the close button
    var closeBtn = $(SEL.close, panel);
    if (closeBtn) closeBtn.focus();
  }

  /* ============================================================
     AJAX — Refresh Drawer Sections
     ============================================================ */
  function refreshDrawer(newCartData) {
    var sectionId = 'cart-drawer-ajax';

    // Dispatch Empire's cartcount:update for header badge sync
    if (newCartData && typeof newCartData.item_count !== 'undefined') {
      state.dispatchingCount = true;
      window.dispatchEvent(new CustomEvent('cartcount:update', {
        detail: { item_count: newCartData.item_count }
      }));
      state.dispatchingCount = false;
      // Also update Empire's data attribute directly
      // Update Empire's header cart badge (uses ::after with content: attr())
      var headerCount = document.querySelector('[data-header-cart-count]');
      if (headerCount) {
        headerCount.setAttribute('data-header-cart-count', newCartData.item_count > 0 ? newCartData.item_count : '');
        headerCount.classList.toggle('visible', newCartData.item_count > 0);
      }
    }

    // Check for confetti trigger (crossed £60 upward)
    var threshold = 6000;
    var oldTotal = state.previousTotal;
    var newTotal = newCartData ? newCartData.total_price : 0;
    var shouldConfetti = (oldTotal < threshold && newTotal >= threshold);
    state.previousTotal = newTotal;

    // Fetch updated drawer HTML
    return fetchHTML('/cart?section_id=' + sectionId + '&view=drawer-ajax')
      .then(function (html) {
        var parser = new DOMParser();
        var doc = parser.parseFromString(html, 'text/html');
        var panel = $(SEL.panel);

        // ---------- Empty → Populated transition ----------
        // When the initial page load had an empty cart, the body has no
        // section containers (items, progress, etc.) — just the empty state.
        // We need to rebuild the full body structure before swapping fragments.
        var hasItemsSection = $(SEL.sectionItems);
        var isNowPopulated = newCartData && newCartData.item_count > 0;

        if (!hasItemsSection && isNowPopulated && panel) {
          // Remove old body (empty state) and checkout placeholder
          var oldBody = $(SEL.body, panel) || panel.querySelector('.cart-drawer__body');
          var oldCheckout = $(SEL.sectionCheckout, panel);
          if (oldBody) oldBody.remove();
          if (oldCheckout) oldCheckout.remove();

          // Build the full scrollable body structure
          var newBody = document.createElement('div');
          newBody.className = 'cart-drawer__body';
          newBody.setAttribute('data-cart-drawer-body', '');
          newBody.innerHTML =
            '<div data-cart-drawer-section="progress"></div>' +
            '<div data-cart-drawer-section="extras"></div>' +
            '<div data-cart-drawer-section="items"></div>' +
            '<div data-cart-drawer-section="loyalty"></div>' +
            '<div data-cart-drawer-section="fbt"></div>' +
            '<div data-cart-drawer-section="signup"></div>';

          // Build checkout footer
          var newCheckout = document.createElement('div');
          newCheckout.setAttribute('data-cart-drawer-section', 'checkout');

          // Insert before the detail panel
          var detailPanel = $(SEL.detailPanel, panel);
          if (detailPanel) {
            panel.insertBefore(newBody, detailPanel);
            panel.insertBefore(newCheckout, detailPanel);
          } else {
            panel.appendChild(newBody);
            panel.appendChild(newCheckout);
          }
        }

        // ---------- Populated → Empty transition ----------
        if (newCartData && newCartData.item_count === 0) {
          var emptyTpl = doc.querySelector('template[data-html="drawer_empty"]');
          var bodyEl = $(SEL.body);
          if (emptyTpl && bodyEl) {
            bodyEl.innerHTML = emptyTpl.innerHTML;
          }
          // Hide checkout footer
          var checkoutSection = $(SEL.sectionCheckout);
          if (checkoutSection) checkoutSection.innerHTML = '';
        }

        // ---------- Swap fragment content ----------
        // Extract and swap each template fragment.
        // FBT is excluded — its content is managed entirely by JS
        // and would be wiped by the empty server-side shell.
        var fragments = {
          'drawer_items': SEL.sectionItems,
          'drawer_progress': SEL.sectionProgress,
          'drawer_checkout': SEL.sectionCheckout,
          'drawer_loyalty': SEL.sectionLoyalty,
          'drawer_extras': SEL.sectionExtras,
          'drawer_signup': SEL.sectionSignup
        };

        // Skip fragment swap if cart is empty (body was already replaced above)
        if (!newCartData || newCartData.item_count > 0) {
          Object.keys(fragments).forEach(function (tplName) {
            var tpl = doc.querySelector('template[data-html="' + tplName + '"]');
            var target = $(fragments[tplName]);
            if (tpl && target) {
              target.innerHTML = tpl.innerHTML;
            }
          });

          // Inject FBT shell if it doesn't exist yet (e.g. after empty→populated rebuild).
          // Normally excluded from the fragment swap to avoid wiping JS-rendered cards,
          // but the shell must exist for fetchFBT() to populate.
          if (!$(SEL.fbtContainer)) {
            var fbtTpl = doc.querySelector('template[data-html="drawer_fbt"]');
            var fbtTarget = $(SEL.sectionFbt);
            if (fbtTpl && fbtTarget) {
              fbtTarget.innerHTML = fbtTpl.innerHTML;
            }
          }
        }

        // Update count badge in drawer header
        var countTpl = doc.querySelector('template[data-html="drawer_count"]');
        var countEl = $(SEL.count);
        if (countTpl && countEl) {
          var newCount = parseInt(countTpl.innerHTML.trim(), 10) || 0;
          countEl.textContent = newCount;
          countEl.style.display = newCount > 0 ? '' : 'none';
        }
        // Ensure count element exists (might not if page loaded with empty cart)
        if (!countEl) {
          var headerLeft = panel ? panel.querySelector('.cart-drawer__header-left') : null;
          if (headerLeft && newCartData && newCartData.item_count > 0) {
            var titleEl = headerLeft.querySelector('.cart-drawer__title');
            var newCountEl = document.createElement('span');
            newCountEl.className = 'cart-drawer__count';
            newCountEl.setAttribute('data-cart-drawer-count', '');
            newCountEl.textContent = newCartData.item_count;
            if (titleEl && titleEl.nextSibling) {
              headerLeft.insertBefore(newCountEl, titleEl.nextSibling);
            } else {
              headerLeft.appendChild(newCountEl);
            }
          }
        }

        // Update config JSON
        var configEl = $(SEL.config);
        if (configEl && newCartData) {
          try {
            var cfg = JSON.parse(configEl.textContent);
            cfg.itemCount = newCartData.item_count;
            cfg.totalPrice = newCartData.total_price;
            cfg.cartNote = newCartData.note || '';
            configEl.textContent = JSON.stringify(cfg);
          } catch (e) { /* ignore */ }
        }

        // Re-bind item events
        bindItemEvents();

        // Re-bind extras events
        bindExtrasEvents();

        // Update delivery tracker
        updateDeliveryTracker();

        // Confetti!
        if (shouldConfetti) {
          fireConfetti();
        }

        // Re-fetch FBT only if the set of cart items changed (add/remove),
        // not on simple qty changes, to preserve card persistence.
        var currentKeys = $$(SEL.item).map(function (el) {
          return el.getAttribute(SEL.itemKey);
        }).sort().join(',');

        if (currentKeys !== state.fbtCartKeys) {
          state.fbtCartKeys = currentKeys;
          fetchFBT();
        }
      })
      .catch(function (err) {
        showError('Something went wrong updating your cart. Please try again.');
        console.error('Cart drawer refresh error:', err);
      })
      .then(function () {
        setLoading(false);
      });
  }

  /* ============================================================
     AJAX — Quantity Change
     ============================================================ */
  function changeQuantity(key, quantity) {
    if (state.isUpdating) return;
    setLoading(true);

    fetchJSON('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (cart) {
        return refreshDrawer(cart);
      })
      .catch(function (err) {
        setLoading(false);
        showError('Could not update quantity. Please try again.');
        console.error('Qty change error:', err);
      });
  }

  function debouncedChangeQuantity(key, quantity) {
    clearTimeout(state.qtyDebounceTimer);
    state.qtyDebounceTimer = setTimeout(function () {
      changeQuantity(key, quantity);
    }, 300);
  }

  /* ============================================================
     AJAX — Remove Item
     ============================================================ */
  function removeItem(key) {
    changeQuantity(key, 0);
  }

  /* ============================================================
     AJAX — Add to Cart (for FBT Quick Add)
     ============================================================ */
  function addToCart(variantId, quantity) {
    if (state.isUpdating) return;
    setLoading(true);

    fetchJSON('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    })
      .then(function () {
        return fetchJSON('/cart.js');
      })
      .then(function (cart) {
        return refreshDrawer(cart);
      })
      .catch(function (err) {
        setLoading(false);
        showError('Could not add item. Please try again.');
        console.error('Add to cart error:', err);
      });
  }

  /* ============================================================
     FBT — Fetch Recommendations
     Logged-in: show past purchases first ("Buy Again"), then
     backfill with recommendations API up to 4 total cards.
     Guest: recommendations API only.
     ============================================================ */
  function fetchFBT() {
    var container = $(SEL.fbtContainer);
    var scroll = $(SEL.fbtScroll);
    if (!container || !scroll) return;

    var config = getConfig();

    // Get current cart variant/product IDs to exclude
    var cartItems = $$(SEL.item);
    var cartVariantIds = cartItems.map(function (el) {
      return el.getAttribute('data-cart-drawer-item-id');
    });

    var maxCards = 12;

    // ---------- Build "Buy Again" cards from past purchases ----------
    var buyAgainCards = [];
    if (config.isLoggedIn && config.pastPurchases && config.pastPurchases.length > 0) {
      config.pastPurchases.forEach(function (pp) {
        if (buyAgainCards.length >= maxCards) return;
        // Skip items already in cart
        if (cartVariantIds.indexOf(String(pp.variantId)) !== -1) return;
        // Skip unavailable
        if (!pp.available) return;
        buyAgainCards.push({
          type: 'buy-again',
          title: pp.title,
          variantId: pp.variantId,
          price: pp.price,
          compareAtPrice: pp.compareAtPrice || 0,
          image: pp.image || '',
          available: pp.available,
          handle: pp.handle || ''
        });
      });
    }

    var slotsRemaining = maxCards - buyAgainCards.length;

    // ---------- Fetch recommendation cards to backfill ----------
    if (slotsRemaining > 0 && cartItems.length > 0) {
      var firstItem = cartItems[0];
      var itemLink = firstItem.querySelector('.cart-drawer__item-title a');
      if (!itemLink) {
        renderFbtCards(container, scroll, buyAgainCards, config.isLoggedIn);
        return;
      }

      var href = itemLink.getAttribute('href');
      var productUrl = href.split('?')[0] + '.json';

      fetch(productUrl)
        .then(function (r) {
          if (!r.ok) throw new Error('Product fetch failed');
          return r.json();
        })
        .then(function (data) {
          var productId = data.product.id;
          return fetch('/recommendations/products.json?product_id=' + productId + '&limit=' + (slotsRemaining + 4) + '&intent=related');
        })
        .then(function (r) {
          if (!r.ok) throw new Error('Recommendations fetch failed');
          return r.json();
        })
        .then(function (data) {
          var recoCards = [];
          if (data.products && data.products.length > 0) {
            // IDs already used in buy-again cards
            var buyAgainVariantIds = buyAgainCards.map(function (c) { return String(c.variantId); });

            data.products.forEach(function (product) {
              if (recoCards.length >= slotsRemaining) return;

              // Skip products already in cart
              var inCart = product.variants.some(function (v) {
                return cartVariantIds.indexOf(String(v.id)) !== -1;
              });
              if (inCart) return;

              // Skip products already in buy-again
              var inBuyAgain = product.variants.some(function (v) {
                return buyAgainVariantIds.indexOf(String(v.id)) !== -1;
              });
              if (inBuyAgain) return;

              var variant = product.variants[0];
              recoCards.push({
                type: 'recommended',
                title: product.title,
                variantId: variant.id,
                price: variant.price,
                compareAtPrice: variant.compare_at_price || 0,
                image: product.featured_image
                  ? product.featured_image.replace(/(\.[^.]+)$/, '_280x$1')
                  : '',
                available: variant.available,
                handle: product.handle || '',
                rating: (product.metafields && product.metafields.reviews && product.metafields.reviews.rating)
                  ? product.metafields.reviews.rating : null,
                ratingCount: (product.metafields && product.metafields.reviews && product.metafields.reviews.rating_count)
                  ? product.metafields.reviews.rating_count : null
              });
            });
          }

          renderFbtCards(container, scroll, buyAgainCards.concat(recoCards), config.isLoggedIn);
        })
        .catch(function () {
          // Reco fetch failed — just show buy-again if we have them
          renderFbtCards(container, scroll, buyAgainCards, config.isLoggedIn);
        });
    } else {
      // No backfill needed (or no cart items for reco seed)
      renderFbtCards(container, scroll, buyAgainCards, config.isLoggedIn);
    }
  }

  /**
   * Render FBT cards into the scroll container.
   * Updates the section title when mixing buy-again + recommended.
   */
  function renderFbtCards(container, scroll, cards, isLoggedIn) {
    if (!cards || cards.length === 0) {
      container.style.display = 'none';
      return;
    }

    // Update section title
    var titleEl = container.querySelector('[data-cart-drawer-fbt-title]');
    if (titleEl) {
      var hasBuyAgain = cards.some(function (c) { return c.type === 'buy-again'; });
      var hasReco = cards.some(function (c) { return c.type === 'recommended'; });
      if (hasBuyAgain && !hasReco) {
        titleEl.textContent = 'Buy again';
      } else if (hasBuyAgain && hasReco) {
        titleEl.textContent = 'Buy again & recommended';
      } else {
        titleEl.textContent = 'You might also like';
      }
    }

    scroll.innerHTML = cards.map(function (card) {
      // Badge
      var badgeHtml = '';
      if (card.type === 'buy-again') {
        badgeHtml = '<span class="cart-drawer__fbt-card-badge cart-drawer__fbt-card-badge--buy-again">Buy Again</span>';
      }

      // Rating — use product data if available, otherwise generate random
      var ratingHtml = '';
      var rating, count;
      if (card.rating) {
        rating = parseFloat(card.rating.value || 0);
        count = card.ratingCount ? card.ratingCount.value : '';
      } else {
        // Randomised: 4, 4.5, or 5 stars with 1-100 review count
        var ratingOptions = [4, 4.5, 5];
        rating = ratingOptions[Math.floor(Math.random() * ratingOptions.length)];
        count = Math.floor(Math.random() * 100) + 1;
      }
      ratingHtml = '<div class="cart-drawer__fbt-card-rating">' +
        '<span class="cart-drawer__fbt-card-stars">' + renderStars(rating) + '</span>' +
        '<span class="cart-drawer__fbt-card-rating-count">(' + count + ')</span>' +
        '</div>';

      // Prices + snack points
      var priceHtml = '<span class="cart-drawer__fbt-card-price">' + formatMoney(card.price) + '</span>';
      if (card.compareAtPrice && card.compareAtPrice > card.price) {
        priceHtml = '<span class="cart-drawer__fbt-card-compare">' + formatMoney(card.compareAtPrice) + '</span>' + priceHtml;
      }
      var cardPoints = Math.floor(card.price / 100);
      if (cardPoints > 0) {
        priceHtml += '<span class="cart-drawer__fbt-card-points">' +
          '<svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>' +
          ' Earn ' + cardPoints + ' pts</span>';
      }

      var handleAttr = card.handle ? ' data-fbt-handle="' + escapeHtml(card.handle) + '"' : '';

      return '<div class="cart-drawer__fbt-card"' + handleAttr + '>' +
        (card.image ? '<img class="cart-drawer__fbt-card-image" src="' + escapeHtml(card.image) + '" alt="' + escapeHtml(card.title) + '" loading="lazy">' : '') +
        '<div class="cart-drawer__fbt-card-body">' +
          badgeHtml +
          '<p class="cart-drawer__fbt-card-title">' + escapeHtml(card.title) + '</p>' +
          ratingHtml +
          '<div class="cart-drawer__fbt-card-prices">' + priceHtml + '</div>' +
          '<div class="cart-drawer__fbt-card-detail" data-fbt-detail="' + card.variantId + '">Show Details</div>' +
          '<button class="cart-drawer__fbt-card-add" data-fbt-add="' + card.variantId + '" ' +
            (card.available ? '' : 'disabled') + '>' +
            (card.available ? 'Quick Add' : 'Sold Out') +
          '</button>' +
        '</div>' +
      '</div>';
    }).join('');

    container.style.display = '';
    bindFbtEvents();
  }

  function renderStars(rating) {
    var full = Math.floor(rating);
    var half = (rating - full) >= 0.5 ? 1 : 0;
    var empty = 5 - full - half;
    var starPath = 'M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z';
    var svg = function (fill) {
      return '<svg viewBox="0 0 24 24" fill="' + fill + '" width="11" height="11"><path d="' + starPath + '"/></svg>';
    };
    var stars = '';
    for (var i = 0; i < full; i++) stars += svg('#FFB800');
    for (var j = 0; j < half; j++) {
      stars += '<svg viewBox="0 0 24 24" width="11" height="11"><defs><linearGradient id="hg"><stop offset="50%" stop-color="#FFB800"/><stop offset="50%" stop-color="#E0E0E0"/></linearGradient></defs><path d="' + starPath + '" fill="url(#hg)"/></svg>';
    }
    for (var k = 0; k < empty; k++) stars += svg('#E0E0E0');
    return stars;
  }

  function formatMoney(pence) {
    var pounds = (pence / 100).toFixed(2);
    return '\u00A3' + pounds;
  }

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str));
    return div.innerHTML;
  }

  /* ============================================================
     Delivery Tracker
     ============================================================ */
  function updateDeliveryTracker() {
    var tracker = $(SEL.delivery);
    var textEl = $(SEL.deliveryText);
    if (!tracker || !textEl) return;

    var now = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
    var hour = now.getHours();
    var minutes = now.getMinutes();
    var day = now.getDay();

    // Sunday: hide
    if (day === 0) {
      tracker.style.display = 'none';
      return;
    }

    tracker.style.display = '';
    tracker.classList.remove('cart-drawer__delivery--urgent', 'cart-drawer__delivery--closed');

    // Saturday cutoff 11am, Mon-Fri cutoff 2pm
    var isSaturday = (day === 6);
    var cutoffHour = isSaturday ? 11 : 14;
    var cutoffLabel = isSaturday ? '11:00 AM' : '2:00 PM';

    if (hour < cutoffHour) {
      var totalMinsLeft = (cutoffHour * 60) - (hour * 60 + minutes);
      var hoursLeft = Math.floor(totalMinsLeft / 60);
      var minsLeft = totalMinsLeft % 60;

      if (totalMinsLeft <= 120) {
        tracker.classList.add('cart-drawer__delivery--urgent');
        textEl.innerHTML = 'Order within <strong>' +
          (hoursLeft > 0 ? hoursLeft + 'h ' : '') + minsLeft + 'min</strong> for dispatch today';
      } else {
        textEl.innerHTML = 'Order before <strong>' + cutoffLabel + '</strong> for dispatch today';
      }
    } else {
      tracker.classList.add('cart-drawer__delivery--closed');
      textEl.innerHTML = 'Order now &mdash; dispatched <strong>next working day</strong>';
    }
  }

  /* ============================================================
     Confetti
     ============================================================ */
  function fireConfetti() {
    var canvas = $(SEL.confetti);
    var panel = $(SEL.panel);
    if (!canvas || !panel) return;

    // Size canvas to the drawer panel
    var rect = panel.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    canvas.style.display = 'block';

    var ctx = canvas.getContext('2d');
    var particles = [];
    var colors = ['#1AC2B9', '#0A7E85', '#F5A623', '#FFD700', '#4ECDC4'];

    for (var i = 0; i < 40; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -10 - Math.random() * 80,
        w: 5 + Math.random() * 5,
        h: 3 + Math.random() * 3,
        vx: (Math.random() - 0.5) * 3,
        vy: 1.5 + Math.random() * 3,
        rotation: Math.random() * 360,
        rotSpeed: (Math.random() - 0.5) * 8,
        color: colors[Math.floor(Math.random() * colors.length)],
        opacity: 1
      });
    }

    var frame = 0;
    var maxFrames = 80;

    function animate() {
      frame++;
      if (frame > maxFrames) {
        canvas.style.display = 'none';
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach(function (p) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08;
        p.rotation += p.rotSpeed;
        if (frame > maxFrames * 0.5) {
          p.opacity = Math.max(0, p.opacity - 0.04);
        }

        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate((p.rotation * Math.PI) / 180);
        ctx.globalAlpha = p.opacity;
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });

      requestAnimationFrame(animate);
    }

    requestAnimationFrame(animate);
  }

  /* ============================================================
     Signup — Server-side Mailchimp + Shopify Subscription
     ============================================================ */
  var SUBSCRIBE_URL = 'https://ninth-victor-421210.nw.r.appspot.com/api/marketing/subscribe';

  function saveCartAttribute(checked) {
    fetchJSON('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: { marketing_opt_in: checked ? 'true' : 'false' }
      })
    }).catch(function () {});
  }

  function submitSubscription(card) {
    var mode = card.getAttribute('data-signup-mode');
    var statusEl = card.querySelector('[data-cart-drawer-signup-status]');
    var submitBtn = card.querySelector('[data-cart-drawer-signup-submit]');

    var email = '';
    var phone = '';
    var customerId = card.getAttribute('data-customer-id') || '';
    var fname = card.getAttribute('data-customer-fname') || '';
    var lname = card.getAttribute('data-customer-lname') || '';

    if (mode === 'guest') {
      var emailInput = card.querySelector('[data-cart-drawer-signup-email]');
      email = emailInput ? emailInput.value.trim() : '';
      if (!email) {
        showStatus(statusEl, 'Please enter your email address', 'error');
        return;
      }
    } else {
      email = card.getAttribute('data-customer-email') || '';
    }

    var phoneInput = card.querySelector('[data-cart-drawer-signup-phone]');
    phone = phoneInput ? phoneInput.value.trim() : '';

    // For SMS-only mode, phone is required
    if (mode === 'sms-only' && !phone) {
      showStatus(statusEl, 'Please enter your phone number', 'error');
      return;
    }

    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = 'Subscribing...';
    }

    saveCartAttribute(true);

    fetch(SUBSCRIBE_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        phone: phone || undefined,
        fname: fname || undefined,
        lname: lname || undefined,
        customer_id: customerId || undefined,
        source: 'cart-drawer'
      })
    })
    .then(function (res) { return res.json(); })
    .then(function (data) {
      if (data.success) {
        var msg = mode === 'sms-only' ? 'Subscribed to SMS deals!' : 'Welcome to the Snack Club!';
        showStatus(statusEl, msg, 'success');
        if (submitBtn) {
          submitBtn.textContent = 'Subscribed';
        }
        // Hide the form after a brief moment
        setTimeout(function () {
          var form = card.querySelector('[data-cart-drawer-signup-form]');
          if (form) {
            form.classList.remove('cart-drawer__signup-form--visible');
            form.classList.add('cart-drawer__signup-form--hidden');
          }
          // For checkbox modes, show checked state permanently
          var check = card.querySelector('[data-cart-drawer-signup-check]');
          if (check) {
            check.classList.add('cart-drawer__signup-check--checked');
          }
        }, 2000);
      } else {
        showStatus(statusEl, data.error || 'Something went wrong', 'error');
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = 'Subscribe';
        }
      }
    })
    .catch(function () {
      showStatus(statusEl, 'Connection error — please try again', 'error');
      if (submitBtn) {
        submitBtn.disabled = false;
        submitBtn.textContent = 'Subscribe';
      }
    });
  }

  function showStatus(el, msg, type) {
    if (!el) return;
    el.textContent = msg;
    el.className = 'cart-drawer__signup-status cart-drawer__signup-status--' + type;
  }

  /* ============================================================
     Event Binding — Cart Items
     ============================================================ */
  function bindItemEvents() {
    // Qty minus
    $$(SEL.qtyMinus).forEach(function (btn) {
      btn.onclick = function () {
        var item = btn.closest(SEL.item);
        if (!item) return;
        var key = item.getAttribute(SEL.itemKey);
        var input = item.querySelector(SEL.qtyInput);
        var qty = parseInt(input.value, 10) || 1;
        if (qty <= 1) return;
        input.value = qty - 1;
        debouncedChangeQuantity(key, qty - 1);
      };
    });

    // Qty plus
    $$(SEL.qtyPlus).forEach(function (btn) {
      btn.onclick = function () {
        var item = btn.closest(SEL.item);
        if (!item) return;
        var key = item.getAttribute(SEL.itemKey);
        var input = item.querySelector(SEL.qtyInput);
        var qty = parseInt(input.value, 10) || 1;
        input.value = qty + 1;
        debouncedChangeQuantity(key, qty + 1);
      };
    });

    // Qty input direct change
    $$(SEL.qtyInput).forEach(function (input) {
      input.onchange = function () {
        var item = input.closest(SEL.item);
        if (!item) return;
        var key = item.getAttribute(SEL.itemKey);
        var qty = parseInt(input.value, 10);
        if (isNaN(qty) || qty < 1) {
          input.value = 1;
          qty = 1;
        }
        debouncedChangeQuantity(key, qty);
      };
    });

    // Remove buttons
    $$(SEL.remove).forEach(function (btn) {
      btn.onclick = function () {
        var item = btn.closest(SEL.item);
        if (!item) return;
        var key = item.getAttribute(SEL.itemKey);
        removeItem(key);
      };
    });
  }

  /* ============================================================
     Event Binding — Extras
     ============================================================ */
  function bindExtrasEvents() {
    // Hide guest login if previously dismissed (runs immediately after DOM swap)
    var loginBox = $(SEL.guestLogin);
    if (loginBox) {
      try {
        if (sessionStorage.getItem('cart_drawer_login_dismissed') === '1') {
          loginBox.remove();
          loginBox = null;
        }
      } catch (e) {}
    }

    // Dismiss guest login button
    var dismissBtn = $(SEL.dismissLogin);
    if (dismissBtn) {
      dismissBtn.onclick = function (e) {
        e.stopPropagation();
        var box = $(SEL.guestLogin);
        if (box) box.remove();
        try { sessionStorage.setItem('cart_drawer_login_dismissed', '1'); } catch (err) {}
      };
    }

    // Signup card — handles three modes: guest, logged-in, sms-only
    var signupCard = $('[data-cart-drawer-signup-card]');
    if (signupCard) {
      var signupMode = signupCard.getAttribute('data-signup-mode');
      var signupForm = signupCard.querySelector('[data-cart-drawer-signup-form]');
      var signupSubmit = signupCard.querySelector('[data-cart-drawer-signup-submit]');

      if (signupMode === 'sms-only') {
        // SMS-only: form is always visible, just wire up submit
        if (signupSubmit) {
          signupSubmit.onclick = function (e) {
            e.stopPropagation();
            submitSubscription(signupCard);
          };
        }
      } else {
        // Guest or logged-in: clicking the row toggles form visibility
        var signupToggle = signupCard.querySelector('[data-cart-drawer-signup-toggle]') || signupCard;
        signupToggle.onclick = function (e) {
          var check = signupCard.querySelector('[data-cart-drawer-signup-check]');
          var isChecked = signupCard.getAttribute('data-checked') === 'true';
          var newState = !isChecked;

          signupCard.setAttribute('data-checked', newState ? 'true' : 'false');
          if (check) {
            check.classList.toggle('cart-drawer__signup-check--checked', newState);
          }

          // Toggle form visibility
          if (signupForm) {
            if (newState) {
              signupForm.classList.remove('cart-drawer__signup-form--hidden');
              signupForm.classList.add('cart-drawer__signup-form--visible');
              // Scroll the form into view after the transition
              setTimeout(function () {
                signupCard.scrollIntoView({ behavior: 'smooth', block: 'end' });
              }, 380);
              // For logged-in users, auto-subscribe email immediately on tick
              if (signupMode === 'logged-in') {
                saveCartAttribute(true);
                submitSubscription(signupCard);
              }
            } else {
              signupForm.classList.remove('cart-drawer__signup-form--visible');
              signupForm.classList.add('cart-drawer__signup-form--hidden');
              saveCartAttribute(false);
            }
          }
        };

        // Wire up submit button for guest form
        if (signupSubmit) {
          signupSubmit.onclick = function (e) {
            e.stopPropagation();
            submitSubscription(signupCard);
          };
        }
      }
    }
  }

  /* ============================================================
     Event Binding — FBT
     ============================================================ */
  function bindFbtEvents() {
    $$('[data-fbt-add]').forEach(function (btn) {
      btn.onclick = function (e) {
        e.stopPropagation();
        var variantId = parseInt(btn.getAttribute('data-fbt-add'), 10);
        if (variantId) {
          btn.disabled = true;
          btn.textContent = 'Adding...';
          addToCart(variantId, 1);
        }
      };
    });

    // "Show Details" links
    $$('[data-fbt-detail]').forEach(function (link) {
      link.onclick = function (e) {
        e.stopPropagation();
        var card = link.closest('.cart-drawer__fbt-card');
        if (!card) return;
        var handle = card.getAttribute('data-fbt-handle');
        var variantId = link.getAttribute('data-fbt-detail');
        openDetailPanel(handle, variantId, card);
      };
    });
  }

  /* ============================================================
     FBT Detail Panel
     ============================================================ */
  function openDetailPanel(handle, variantId, cardEl) {
    var panel = $(SEL.detailPanel);
    if (!panel) return;

    // Pre-populate from the card while we fetch full details
    var cardImg = cardEl ? cardEl.querySelector('.cart-drawer__fbt-card-image') : null;
    var cardTitle = cardEl ? cardEl.querySelector('.cart-drawer__fbt-card-title') : null;
    var cardPrice = cardEl ? cardEl.querySelector('.cart-drawer__fbt-card-price') : null;

    var imgEl = $(SEL.detailImg);
    var titleEl = $(SEL.detailTitle);
    var priceEl = $(SEL.detailPrice);
    var descEl = $(SEL.detailDesc);
    var linkEl = $(SEL.detailLink);
    var addBtn = $(SEL.detailAdd);

    if (imgEl && cardImg) imgEl.src = cardImg.src;
    if (titleEl && cardTitle) titleEl.textContent = cardTitle.textContent;
    if (priceEl && cardPrice) priceEl.textContent = cardPrice.textContent;
    if (descEl) descEl.textContent = 'Loading product details...';
    if (linkEl) linkEl.href = '#';
    if (addBtn) {
      addBtn.setAttribute('data-detail-variant', variantId || '');
      addBtn.disabled = false;
      addBtn.textContent = 'Add to Cart';
    }

    // Open the panel
    panel.classList.add('is-open');

    // Fetch full product details if we have a handle
    if (handle) {
      fetch('/products/' + handle + '.json')
        .then(function (r) {
          if (!r.ok) throw new Error('Product fetch failed');
          return r.json();
        })
        .then(function (data) {
          var product = data.product;
          if (titleEl) titleEl.textContent = product.title;
          if (imgEl && product.image && product.image.src) {
            imgEl.src = product.image.src;
          }
          if (descEl) {
            // Strip HTML tags from description
            var tmp = document.createElement('div');
            tmp.innerHTML = product.body_html || '';
            descEl.textContent = tmp.textContent || tmp.innerText || 'No description available.';
          }
          if (linkEl) linkEl.href = '/products/' + handle;

          // Find matching variant price
          if (product.variants && product.variants.length > 0) {
            var v = product.variants[0];
            for (var i = 0; i < product.variants.length; i++) {
              if (String(product.variants[i].id) === String(variantId)) {
                v = product.variants[i];
                break;
              }
            }
            if (priceEl) {
              // Product JSON returns prices in pounds (e.g. "6.15"), formatMoney expects pence
              var pricePence = Math.round(parseFloat(v.price) * 100);
              var comparePence = v.compare_at_price ? Math.round(parseFloat(v.compare_at_price) * 100) : 0;
              var priceStr = formatMoney(pricePence);
              if (comparePence && comparePence > pricePence) {
                priceStr = '<span style="text-decoration:line-through;color:#999;font-size:0.875rem;margin-right:6px;">' + formatMoney(comparePence) + '</span>' + priceStr;
              }
              priceEl.innerHTML = priceStr;
            }
            if (addBtn) {
              addBtn.setAttribute('data-detail-variant', v.id);
              addBtn.disabled = !v.available;
              addBtn.textContent = v.available ? 'Add to Cart' : 'Sold Out';
            }
          }
        })
        .catch(function () {
          if (descEl) descEl.textContent = 'Could not load product details.';
        });
    } else if (!handle && variantId) {
      // No handle — try to build a link from the card's title
      if (linkEl) linkEl.style.display = 'none';
    }
  }

  function closeDetailPanel() {
    var panel = $(SEL.detailPanel);
    if (panel) panel.classList.remove('is-open');
  }

  /* ============================================================
     Global Event Listeners
     ============================================================ */
  function init() {
    // Close button(s)
    $$(SEL.close).forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        close();
      });
    });

    // Overlay click
    var overlay = $(SEL.overlay);
    if (overlay) {
      overlay.addEventListener('click', close);
    }

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && state.isOpen) {
        close();
      }
    });

    // Focus trap
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || !state.isOpen) return;
      if (!state.focusTrapFirst || !state.focusTrapLast) return;

      if (e.shiftKey) {
        if (document.activeElement === state.focusTrapFirst) {
          e.preventDefault();
          state.focusTrapLast.focus();
        }
      } else {
        if (document.activeElement === state.focusTrapLast) {
          e.preventDefault();
          state.focusTrapFirst.focus();
        }
      }
    });

    // Cart icon trigger(s)
    $$(SEL.trigger).forEach(function (el) {
      el.addEventListener('click', function (e) {
        e.preventDefault();
        open(el);
      });
    });

    // ---- Universal ATC interception ----
    // Intercept ALL add-to-cart calls (fetch + XHR) so we catch every
    // touchpoint: product page form, quick buy, quickshop, re-order,
    // third-party apps, etc.

    var atcDebounceTimer = null;
    function onCartAddDetected() {
      // Ignore if we're already updating (our own FBT/qty calls)
      if (state.isUpdating) return;
      // Respect the close cooldown
      if (Date.now() - state.closedAt < 800) return;

      // Debounce: multiple listeners may fire for the same ATC action
      // (e.g. XHR intercept + cartcount:update event)
      clearTimeout(atcDebounceTimer);
      atcDebounceTimer = setTimeout(function () {
        // Re-check in case our own code started an update during the delay
        if (state.isUpdating) return;
        fetchJSON('/cart.js').then(function (cart) {
          refreshDrawer(cart).then(function () {
            if (!state.isOpen) open();
          });
        }).catch(function () {});
      }, 300);
    }

    // 1. Intercept fetch() — catches modern code paths
    var originalFetch = window.fetch;
    window.fetch = function () {
      var url = arguments[0];
      var opts = arguments[1];
      var urlStr = typeof url === 'string' ? url : (url && url.url ? url.url : '');
      var method = (opts && opts.method) ? opts.method.toUpperCase() : 'GET';

      var result = originalFetch.apply(this, arguments);

      if (method === 'POST' && urlStr.indexOf('/cart/add') !== -1) {
        result.then(function (response) {
          if (response.ok) {
            onCartAddDetected();
          }
          return response;
        }).catch(function () {});
      }

      return result;
    };

    // 2. Intercept XMLHttpRequest — catches jQuery.ajax (Empire's product form ATC uses this)
    var originalXHROpen = XMLHttpRequest.prototype.open;
    var originalXHRSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this._cartDrawerMethod = method;
      this._cartDrawerUrl = url;
      return originalXHROpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (xhr._cartDrawerMethod &&
          xhr._cartDrawerMethod.toUpperCase() === 'POST' &&
          xhr._cartDrawerUrl &&
          xhr._cartDrawerUrl.indexOf('/cart/add') !== -1) {
        xhr.addEventListener('load', function () {
          if (xhr.status >= 200 && xhr.status < 300) {
            onCartAddDetected();
          }
        });
      }
      return originalXHRSend.apply(this, arguments);
    };

    // 3. Still listen for cartcount:update as a safety net
    window.addEventListener('cartcount:update', function () {
      if (state.dispatchingCount) return;
      onCartAddDetected();
    });

    // 4. Custom cart:added event (for any custom scripts)
    document.addEventListener('cart:added', function () {
      onCartAddDetected();
    });

    // Detail panel — back button
    var detailBackBtn = $(SEL.detailBack);
    if (detailBackBtn) {
      detailBackBtn.addEventListener('click', function (e) {
        e.preventDefault();
        closeDetailPanel();
      });
    }

    // Detail panel — add to cart button
    var detailAddBtn = $(SEL.detailAdd);
    if (detailAddBtn) {
      detailAddBtn.addEventListener('click', function () {
        var variantId = parseInt(detailAddBtn.getAttribute('data-detail-variant'), 10);
        if (variantId) {
          detailAddBtn.disabled = true;
          detailAddBtn.textContent = 'Adding...';
          addToCart(variantId, 1);
          // Close detail panel after a brief delay
          setTimeout(function () {
            closeDetailPanel();
          }, 500);
        }
      });
    }

    // Bind initial item events
    bindItemEvents();

    // Bind extras events
    bindExtrasEvents();
  }

  /* ============================================================
     Boot
     ============================================================ */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* ============================================================
     Quickshop Delivery Tracker Observer
     ---------------------------------------------------------------
     The delivery-tracker.liquid snippet contains an inline <script>
     that won't execute when injected via jQuery .html() (jQuery 3.x).
     This observer detects dynamically-added [data-delivery-tracker]
     elements (e.g. from quickshop modals) and initialises them.
     ============================================================ */
  function initProductDeliveryTracker(tracker) {
    var textEl = tracker.querySelector('[data-delivery-text]');
    if (!textEl) return;
    // Skip if already initialised
    if (tracker.getAttribute('data-dt-init')) return;
    tracker.setAttribute('data-dt-init', '1');

    function getUKTime() {
      return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/London' }));
    }

    function update() {
      var now = getUKTime();
      var hour = now.getHours();
      var minutes = now.getMinutes();
      var day = now.getDay();

      if (day === 0) { tracker.style.display = 'none'; return; }
      tracker.style.display = '';
      tracker.classList.remove('delivery-tracker--urgent', 'delivery-tracker--closed');

      var isSaturday = (day === 6);
      var cutoffHour = isSaturday ? 11 : 14;
      var cutoffLabel = isSaturday ? '11:00 AM' : '2:00 PM';

      if (hour < cutoffHour) {
        var totalMinsLeft = (cutoffHour * 60) - (hour * 60 + minutes);
        var hoursLeft = Math.floor(totalMinsLeft / 60);
        var minsLeft = totalMinsLeft % 60;
        if (totalMinsLeft <= 120) {
          tracker.classList.add('delivery-tracker--urgent');
          textEl.innerHTML = 'Order within <strong>' +
            (hoursLeft > 0 ? hoursLeft + 'h ' : '') + minsLeft + 'min</strong> to have your order dispatched today';
        } else {
          textEl.innerHTML = 'Order before <strong>' + cutoffLabel + '</strong> to have your order dispatched today';
        }
      } else {
        tracker.classList.add('delivery-tracker--closed');
        textEl.innerHTML = 'Order now &mdash; your order will be dispatched next working day';
      }
    }

    update();
    setInterval(update, 30000);
  }

  if (window.MutationObserver) {
    var dtObserver = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-delivery-tracker')) {
            initProductDeliveryTracker(node);
          } else if (node.querySelectorAll) {
            var nested = node.querySelectorAll('[data-delivery-tracker]');
            for (var k = 0; k < nested.length; k++) {
              initProductDeliveryTracker(nested[k]);
            }
          }
        }
      }
    });

    function startDtObserver() {
      dtObserver.observe(document.body, { childList: true, subtree: true });
    }

    if (document.body) {
      startDtObserver();
    } else {
      document.addEventListener('DOMContentLoaded', startDtObserver);
    }
  }
})();
